import React, { useEffect, useRef, useState } from "react";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError, API_BASE } from "../lib/api";
import useMicRecorder from "../lib/useMicRecorder";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Send, HeartPulse, Volume2, Square, Mic } from "lucide-react";
import { toast } from "sonner";

function splitCitations(text) {
  if (!text) return { body: "", sources: [] };
  const re = /(?:^|\n)\s*(?:Sumber|Source)\s*:\s*([^\n]+)\s*$/i;
  const m = text.match(re);
  if (!m) return { body: text.trim(), sources: [] };
  const raw = m[1];
  const sources = raw.split(/[,;/]/).map((s) => s.trim()).filter(Boolean);
  return { body: text.slice(0, m.index).trim(), sources };
}

export default function Chat() {
  const { t, lang } = useLang();
  const [sessionId] = useState("main");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const bottomRef = useRef(null);
  const sendRef = useRef(null);

  const { recording, transcribing, start, stop } = useMicRecorder({
    language: lang,
    onTranscript: ({ text, audio_b64, audio_mime }) => {
      // Auto-send when the user finished speaking, with audio attached
      setTimeout(() => sendRef.current?.(text, { audio_b64, audio_mime, voice_reply: true }), 60);
    },
  });

  const greet = lang === "id"
    ? "Halo, saya Gracely. Ada yang ingin kamu bicarakan hari ini?"
    : "Hi, I'm Gracely. What's on your mind today?";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/history", { params: { session_id: sessionId } });
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map((m) => ({ id: m.id, role: m.role, content: m.content, has_audio: m.has_audio })));
        } else {
          setMessages([{ role: "assistant", content: greet }]);
        }
      } catch {
        setMessages([{ role: "assistant", content: greet }]);
      }
    })();
  }, [sessionId, greet, lang]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (overrideText, extras = {}) => {
    const msg = (overrideText ?? input).trim();
    if (!msg || sending) return;
    setMessages((prev) => [...prev, { role: "user", content: msg, has_audio: !!extras.audio_b64 }]);
    setInput("");
    setSending(true);
    try {
      const body = { session_id: sessionId, message: msg, lang };
      if (extras.audio_b64) {
        body.user_audio_b64 = extras.audio_b64;
        body.user_audio_mime = extras.audio_mime;
        body.voice_reply = !!extras.voice_reply;
      }
      const { data } = await api.post("/chat/message", body);
      setMessages((prev) => {
        const next = [...prev];
        // attach id + audio to the last user message
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === "user" && !next[i].id) {
            next[i] = { ...next[i], id: data.user_message_id, has_audio: data.has_user_audio };
            break;
          }
        }
        next.push({ id: data.assistant_message_id, role: "assistant", content: data.reply, has_audio: data.has_assistant_audio });
        return next;
      });
      // Auto-play assistant audio for voice conversation
      if (extras.voice_reply && data.has_assistant_audio) {
        setTimeout(() => playMessageAudio(data.assistant_message_id), 200);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };
  sendRef.current = send;

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const playMessageAudio = async (id) => {
    if (playingId === id) return stopAudio();
    stopAudio();
    setPlayingId(id);
    try {
      const token = localStorage.getItem("gracely_token");
      const res = await fetch(`${API_BASE}/chat/audio/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("no audio");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.play();
    } catch {
      setPlayingId(null);
    }
  };

  // Speaker for messages without saved audio: fall back to on-demand TTS
  const speakText = async (id, text) => {
    if (playingId === id) return stopAudio();
    stopAudio();
    setPlayingId(id);
    try {
      const token = localStorage.getItem("gracely_token");
      const res = await fetch(`${API_BASE}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, voice: "nova" }),
      });
      if (!res.ok) throw new Error("TTS failed");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.play();
    } catch {
      setPlayingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-80px)]" data-testid="chat-page">
      <div className="flex items-center gap-3 pb-6">
        <div className="w-10 h-10 rounded-full bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center">
          <HeartPulse className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-serif-display text-3xl text-stone-900">{t("chat_title")}</h1>
          <p className="text-xs text-stone-500">{lang === "id" ? "Asisten kesehatan empati • bukan diagnosis" : "Empathetic health assistant • not a diagnosis"}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4 pr-2">
        {messages.map((m, i) => {
          const { body, sources } = m.role === "assistant" ? splitCitations(m.content) : { body: m.content, sources: [] };
          const key = m.id || `local-${i}`;
          const canPlaySaved = m.id && m.has_audio;
          const isPlaying = playingId === m.id;
          return (
            <div key={key} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-stone-100 text-stone-900" : "bg-[#EAF0E4] text-stone-900"
                }`}
                data-testid={m.role === "assistant" ? `msg-assistant-${i}` : `msg-user-${i}`}
              >
                <div className="flex items-center justify-between mb-1 gap-3">
                  {m.role === "assistant" ? (
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#4A5D23]">
                      <Sparkles className="w-3 h-3" /> Gracely
                    </div>
                  ) : (
                    m.has_audio && (
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-500">
                        <Mic className="w-3 h-3" /> {lang === "id" ? "Suaramu" : "Your voice"}
                      </div>
                    )
                  )}
                  {(canPlaySaved || m.role === "assistant") && (
                    <button
                      onClick={() => canPlaySaved ? playMessageAudio(m.id) : speakText(key, body)}
                      className={`ml-auto ${m.role === "user" ? "text-stone-600" : "text-[#4A5D23]"} hover:opacity-70`}
                      aria-label="Play"
                      data-testid={`play-btn-${i}`}
                    >
                      {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <div className="text-[15px]">{body}</div>
                {sources.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {sources.map((s, si) => (
                      <span key={si} className="text-[10px] font-bold uppercase tracking-wide bg-white/70 text-[#4A5D23] px-2 py-0.5 rounded-full border border-[#4A5D23]/20" data-testid={`citation-${i}-${si}`}>
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#EAF0E4] px-4 py-3 rounded-2xl text-sm text-stone-600 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#4A5D23] animate-pulse" />
              {t("thinking")}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-3 flex gap-2 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        <button
          onClick={() => (recording ? stop() : start())}
          disabled={transcribing}
          className={`w-10 h-10 rounded-full flex items-center justify-center btn-pop flex-shrink-0 ${recording ? "bg-[#E07A5F] text-white animate-pulse" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
          aria-label="Mic"
          data-testid="mic-btn"
        >
          <Mic className="w-4 h-4" />
        </button>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder={transcribing ? (lang === "id" ? "Menerjemahkan suara…" : "Transcribing…") : t("chat_placeholder")}
          className="border-transparent focus-visible:ring-0 resize-none min-h-[40px]"
          data-testid="chat-input"
        />
        <Button onClick={() => send()} disabled={sending || !input.trim()} className="rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white btn-pop" data-testid="chat-send-btn">
          <Send className="w-4 h-4 mr-1" /> {t("send")}
        </Button>
      </div>
    </div>
  );
}
